import React, {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState
} from 'react';
import PropTypes from 'prop-types';
import { useDrawerNavigation } from '../context/DrawerNavigationContext';
import useFocusTrap from '../hooks/useFocusTrap';
import useSwipeBack from '../hooks/useSwipeBack';
import useRefMap from '../hooks/useRefMap';
import { DRILLDOWN_DIALOG_ID } from '../global-constants';
import { SlidePanel } from './SlidePanel';
import SubMenuPanel from './SubMenuPanel';

/**
 * DrilldownPanel — the drilldown submenu as a single keep-mounted SlidePanel
 * holding one lazily-mounted SubMenuPanel body per visited parent item.
 *
 * Unlike the previous react-bootstrap Modal (which swapped its single child by
 * active item, remounting — and so re-bootstrapping every embedded fragment —
 * on each drilldown open), this keeps every visited body mounted for the life
 * of the page (the mega menu's `mountedMenuIds` pattern) and only toggles which
 * one is visible. Drilling A → B → A never remounts A's body, so its fragment
 * widget never re-runs its bundle — this is what removes the drawer's
 * per-reopen node/listener staircase.
 *
 * Must be rendered inside DrawerNavigationProvider and DrawerContext.Provider.
 *
 * @param {Object} props
 * @param {string} props.dockClass - 'dock-left' or 'dock-right'
 * @param {string} props.drawerDirection - 'left' or 'right'
 * @param {boolean} [props.isMobile]
 * @returns {React.ReactElement}
 */
const DrilldownPanel = ({ dockClass, drawerDirection, isMobile }) => {
	const { activeDrillDownItem, onSubmenuClose, triggerRef } =
		useDrawerNavigation();
	const containerRef = useRef(null);

	const isOpen = activeDrillDownItem !== null;

	// Every parent item drilled into at least once, keyed by id. Never pruned,
	// so re-drilling an item reuses its already-mounted body (leak fix).
	const [mountedItems, setMountedItems] = useState(() => new Map());
	useEffect(() => {
		if (!activeDrillDownItem) return;
		setMountedItems((prev) => {
			if (prev.has(activeDrillDownItem.id)) return prev;
			const next = new Map(prev);
			next.set(activeDrillDownItem.id, activeDrillDownItem);
			return next;
		});
	}, [activeDrillDownItem]);

	// Which body stays visible. Tracks the last active id (not activeDrillDownItem
	// directly) so the closing body remains visible through the slide-out, when
	// activeDrillDownItem is already null.
	const [visibleBodyId, setVisibleBodyId] = useState(null);
	useEffect(() => {
		if (activeDrillDownItem) setVisibleBodyId(activeDrillDownItem.id);
	}, [activeDrillDownItem]);

	const backRefs = useRefMap(Array.from(mountedItems.keys()));

	useFocusTrap(containerRef, isOpen);

	const swipeDirection = drawerDirection === 'left' ? 'left' : 'right';
	useSwipeBack(containerRef, onSubmenuClose, swipeDirection, isOpen);

	// Ordered close for a11y (Q7): the instant the drilldown closes, move focus
	// back to the trigger — BEFORE the SlidePanel goes inert on this same commit
	// — so focus never sits inside an inert/hidden subtree (which is what caused
	// Chrome's "Blocked aria-hidden … descendant retained focus" error).
	const wasOpenRef = useRef(false);
	useLayoutEffect(() => {
		if (isOpen) {
			wasOpenRef.current = true;
		} else if (wasOpenRef.current) {
			wasOpenRef.current = false;
			triggerRef.current?.focus();
		}
	}, [isOpen, triggerRef]);

	// On open (after the slide-in), focus the active body's own back button.
	const handleEntered = useCallback(() => {
		const activeId = activeDrillDownItem?.id;
		if (activeId) backRefs.current[activeId]?.current?.focus();
	}, [activeDrillDownItem, backRefs]);

	return (
		<SlidePanel
			id={DRILLDOWN_DIALOG_ID}
			isOpen={isOpen}
			isActive={isOpen}
			dockClass={dockClass}
			onClose={onSubmenuClose}
			onEntered={handleEntered}
			backdrop={null}
			baseClassName="ws-nav-drawer-drilldown"
			className={isMobile ? 'ws-nav-drawer-drilldown--mobile' : ''}
			ariaLabel={
				activeDrillDownItem?.label
					? `${activeDrillDownItem.label} submenu`
					: 'Submenu'
			}
		>
			<div
				id="ws-nav-drawer-drilldown-panel"
				ref={containerRef}
				className="ws-nav-drawer__drilldown-container d-flex flex-column flex-grow-1 h-100"
				data-testid="drilldown-panel"
			>
				{Array.from(mountedItems.values()).map((item) => (
					<SubMenuPanel
						key={item.id}
						activeItem={item}
						isActive={visibleBodyId === item.id}
						onSubmenuClose={onSubmenuClose}
						backRef={backRefs.current[item.id]}
					/>
				))}
			</div>
		</SlidePanel>
	);
};

DrilldownPanel.propTypes = {
	dockClass: PropTypes.string.isRequired,
	drawerDirection: PropTypes.string.isRequired,
	isMobile: PropTypes.bool
};

DrilldownPanel.defaultProps = {
	isMobile: false
};

export { DrilldownPanel };
